import { Building2, CalendarRange, Layers, MapPin, RotateCcw } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { ScopeChip, ScopeKey } from '@/hooks/useContextScope';

const ICONS: Record<ScopeKey, React.ReactNode> = {
  financialYearCode: <CalendarRange size={10} />,
  locationId:        <MapPin size={10} />,
  costCenterId:      <Layers size={10} />,
};

interface Props {
  chips:    ScopeChip[];
  canReset: boolean;
  onReset:  () => void;
}

/** Scope row of a transaction list: one chip per filter actually applied, plus "Reset to my context". */
export const ScopeChips = ({ chips, canReset, onReset }: Props) => {
  if (chips.length === 0 && !canReset) return null;

  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap">
      <div className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 font-medium uppercase tracking-wider">
        <Building2 size={10} />
        Scope:
      </div>
      {chips.length === 0 && (
        <span className="text-[11px] text-slate-500 dark:text-slate-400">All years, locations and cost centres</span>
      )}
      {chips.map(chip => (
        <span
          key={chip.key}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium',
            chip.locked
              ? 'bg-orange-50 dark:bg-orange-900/25 text-orange-700 dark:text-orange-300 ring-1 ring-orange-200 dark:ring-orange-800'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-600',
          )}
        >
          {ICONS[chip.key]}
          {chip.label}
          {chip.locked && <span className="text-[9px] text-orange-400 dark:text-orange-500 ml-0.5">🔒</span>}
          {chip.onClear && (
            <button
              type="button"
              onClick={chip.onClear}
              className="ml-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 leading-none"
              title="Remove this filter"
              aria-label={`Remove filter ${chip.label}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {canReset && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <RotateCcw size={10} />
          Reset to my context
        </button>
      )}
    </div>
  );
};
